import { create } from 'zustand';
import { readStorage, writeStorage } from '../lib/storage';
import {
  DEFAULT_LANGUAGE,
  MESSAGES_PT_BR,
  loadMessages,
  type Language,
  type MessageKey,
  normalizeLanguage,
} from './messages';

const LANG_KEY = 'sorteia:lang';

type Table = Record<MessageKey, string>;

type I18nState = {
  language: Language;
  table: Table;
  setLanguage: (language: Language) => void;
  adoptServerLocale: (locale: string | null | undefined) => void;
  t: (key: MessageKey) => string;
};

function initialLanguage(): Language {
  const stored = readStorage(localStorage, LANG_KEY);
  if (stored) return normalizeLanguage(stored);
  return normalizeLanguage(typeof navigator === 'undefined' ? DEFAULT_LANGUAGE : navigator.language);
}

function applyDocumentLanguage(language: Language) {
  try {
    document.documentElement.lang = language;
  } catch {
    // Document may not exist during non-browser evaluation.
  }
}

const initial = initialLanguage();
applyDocumentLanguage(initial);

const PT = MESSAGES_PT_BR as unknown as Table;

/**
 * A fresh `t` per table.
 *
 * This identity is load-bearing: components subscribe with
 * `useI18nStore((state) => state.t)`, and zustand only re-renders them when the
 * selected value changes. A `t` that closes over `get()` never changes identity,
 * so swapping the table left every such component showing the previous locale
 * until it unmounted — switching language on a page appeared to do nothing until
 * you navigated away and back. Rebuilding `t` alongside `table` makes the
 * subscription enough on its own; no component needs to also select `language`.
 */
function makeT(table: Table): (key: MessageKey) => string {
  return (key) => table[key] || PT[key] || key;
}

export const useI18nStore = create<I18nState>((set, get) => ({
  language: initial,
  // pt-BR is in the entry chunk and is also the fallback, so there is always a
  // table to read. A non-default locale swaps this in once its chunk lands.
  table: PT,
  setLanguage: (language) => {
    writeStorage(localStorage, LANG_KEY, language);
    applyDocumentLanguage(language);
    // Set the label immediately so the switcher feels instant, then swap the
    // table when it arrives. Between the two, `t` still answers from pt-BR
    // rather than rendering raw keys.
    set({ language });
    void loadMessages(language).then((table) => {
      // The user may have switched again while this was in flight.
      if (get().language === language) set({ table, t: makeT(table) });
    });
  },
  /**
   * Apply `users.locale` from the server — but only on a device that has never
   * chosen a language.
   *
   * The point is the new-device case: someone who registered in Spanish and signs
   * in on a fresh phone should not land in Portuguese just because that is the
   * bundle default.
   *
   * Deliberately does **not** override a stored choice. `users.locale` is written
   * once at registration and there is no endpoint to update it, so it is a
   * registration-time snapshot, not a live preference. Letting it win on every
   * login would pin players to the language they signed up in and quietly undo
   * every later switch — the switcher would look broken after a reload.
   */
  adoptServerLocale: (locale) => {
    if (!locale) return;
    if (readStorage(localStorage, LANG_KEY)) return;
    const normalized = normalizeLanguage(locale);
    if (normalized === get().language) return;
    get().setLanguage(normalized);
  },
  t: makeT(PT),
}));

/**
 * Resolve the persisted locale before the first render.
 *
 * Without this, a user who has chosen en or es gets a frame of Portuguese on
 * every cold load — the table is only swapped in asynchronously. main.tsx
 * awaits this, which costs nothing for pt-BR (already bundled, resolves on the
 * spot) and one small chunk fetch for everyone else.
 */
export async function preloadLanguage(): Promise<void> {
  const language = useI18nStore.getState().language;
  if (language === DEFAULT_LANGUAGE) return;
  try {
    const table = await loadMessages(language);
    if (useI18nStore.getState().language === language) useI18nStore.setState({ table, t: makeT(table) });
  } catch {
    // A failed chunk must not block boot — pt-BR is a complete table.
  }
}
